import type { AgentFocus } from '@tunasong/agent-context'
import type { AgentStreamPayload } from '@tunasong/redux'
import type { VirtualElement } from '@tunasong/ui-lib'
import { useSyncExternalStore } from 'react'

/**
 * An open inline ask: the compact field the musician types into, anchored at a block (spec
 * `2026-07-23-inline-in-context-agent-interactions-design.md`, InlineAskField). Opened by an affordance or
 * block action that has no canned ask; submitting starts an interactive turn at the same anchor.
 */
export interface InlineAsk {
  agentId: string
  /** The agent's display name, for the field's label and placeholder ("Ask Nils"). */
  agentName: string
  /** What the ask is about: the targeted block. */
  focus?: AgentFocus
  trigger: NonNullable<AgentStreamPayload['trigger']>
  /** Placement anchor; the answer note or edit ghost renders here too. */
  anchorEl: HTMLElement | VirtualElement | null
  /** What the ask is about, for the docked field's chip on a phone ("About: Verse"; else "this line"). */
  aboutLabel?: string
}

type Listener = () => void

const listeners = new Set<Listener>()
let open: InlineAsk | null = null

const notify = () => {
  for (const listener of listeners) {
    listener()
  }
}

/** Open the ask field (one at a time: a new one replaces the old). Mirrors `setActiveInlineTurn`. */
export const openInlineAsk = (ask: InlineAsk): void => {
  open = ask
  notify()
}

export const closeInlineAsk = (): void => {
  open = null
  notify()
}

const subscribe = (listener: Listener): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const getSnapshot = (): InlineAsk | null => open

/** The open ask field, or `null`. */
export const useInlineAsk = (): InlineAsk | null => useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
